import React from 'react';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { AttentionDot } from './ui/AttentionDot';
import { SWAP_MOTION } from '@components/live/liveMotion';
import { cn } from '@lib/utils';
import { countOtherSpaces } from '@utils/buildSpaceEventItems';

export interface SpaceIndicatorItem {
  id: string;
  name: string;
  image_url: string | null;
  type: 'space' | 'event';
  /**
   * Live Layer v1 (L12): set true on the event item that `buildSpaceEventItems`
   * prepends while the author's event is live. Flips the indicator's first slot
   * into its "at {event}" live state (pulsing `mb-live` dot). Absent everywhere
   * else, so non-boosted indicators render exactly as before.
   */
  live?: boolean;
}

interface SpaceIndicatorProps {
  items: SpaceIndicatorItem[];
  onItemClick?: (item: SpaceIndicatorItem, e: React.MouseEvent) => void;
  className?: string;
  /**
   * Override the default 18×18 avatar size. Useful when the indicator is used
   * in larger contexts (e.g. profile question card needs a 22px avatar to
   * match Figma 3257:11895).
   */
  avatarClassName?: string;
}

const NAME_CLASSES = 'text-xs font-medium text-mb-charcoal truncate min-w-0';

/** Circular space/event avatar (18px default) shared by both indicator faces. */
function IndicatorAvatar({
  item,
  avatarClassName,
}: {
  item: SpaceIndicatorItem;
  avatarClassName?: string;
}) {
  return (
    <Avatar className={cn('w-[18px] h-[18px] shrink-0', avatarClassName)}>
      {/* `object-cover` matches the previous <img>-based rendering — without it,
          non-square space/event images get distorted by AvatarImage's default
          object-fit. (Copilot review on PR #505 surfaced this.) Both faces show the
          name beside the avatar, so the avatar adds nothing to the button's name
          (TECH-718): the image has alt="" and the initial standing in for it is hidden. */}
      <AvatarImage
        src={item.image_url ?? undefined}
        alt=""
        crossOrigin="anonymous"
        className="object-cover"
      />
      <AvatarFallback
        aria-hidden="true"
        className="text-[8px] font-medium bg-neutral-200 text-neutral-600"
      >
        {item.name.charAt(0).toUpperCase()}
      </AvatarFallback>
    </Avatar>
  );
}

/**
 * Inline indicator showing which space/event a question belongs to.
 * Displays the first item's circular image + name, with "+N" for the other
 * Spaces the question is in. Events are not counted (countOtherSpaces).
 * Replaces the old EventBadgeList ghost-measurement pattern.
 *
 * Live Layer v1 (L12): when `items[0].live`, the first slot renders the live
 * "at {event}" state — a pulsing `mb-live` dot + "at {name}" — cross-fading
 * from the underlying space face (§4.1). While live the "+N" overflow is
 * hidden (ruling 2026-07-10): the row is purely about the event, and its tap
 * routes straight to the event page — so the count wouldn't be inspectable
 * there, unlike the non-live row whose tap opens the SpacesSheet. The space
 * face still renders as the cross-fade underlay, and the whole button still
 * taps through `onItemClick(items[0])`.
 */
export const SpaceIndicator: React.FC<SpaceIndicatorProps> = ({
  items,
  onItemClick,
  className,
  avatarClassName,
}) => {
  // Liveness comes purely from the first item — the host sets `author_live_event`
  // only during the live window, so there is no client tick here. The
  // `items.length` guard keeps the index access safe before the empty early
  // return below (hooks must run first, so the return can't lead).
  const isLive = items.length > 0 && Boolean(items[0].live);

  // Cross-fade the live face in ONLY on a genuine in-lifetime false→true flip
  // (§4.1: "fires only at phase boundaries"): mounting already-live renders
  // SETTLED immediately, so a feed scrolled during a live window doesn't
  // shimmer every indicator (W2-5 M1). On a flip, mount the hidden pose and
  // settle one frame later so the CSS transition actually runs. No client
  // tick — liveness is driven purely by `items` (the host sets
  // `author_live_event` only during the live window).
  const [entered, setEntered] = React.useState(isLive);
  const prevLiveRef = React.useRef(isLive);
  React.useEffect(() => {
    const wasLive = prevLiveRef.current;
    prevLiveRef.current = isLive;
    if (!isLive) {
      // Window ended: reset so the next flip replays the fade.
      setEntered(false);
      return undefined;
    }
    if (wasLive) {
      // Mounted live (ref initialised true) or continuously live — stay settled.
      return undefined;
    }
    const raf = requestAnimationFrame(() => {
      setEntered(true);
    });
    return () => {
      cancelAnimationFrame(raf);
    };
  }, [isLive]);

  if (items.length === 0) return null;

  const firstItem = items[0];
  const overflowCount = countOtherSpaces(items);
  // The underlying space face — continuous across the live↔non-live boundary
  // (items[1] while the event is prepended, items[0] once the window ends);
  // undefined for the rare boosted post that carries no space at all.
  let spaceFace: SpaceIndicatorItem | undefined = firstItem;
  if (isLive) {
    spaceFace = items.length > 1 ? items[1] : undefined;
  }

  return (
    <button
      type="button"
      className={cn(
        'flex items-center gap-1 min-w-0 max-w-full overflow-hidden',
        onItemClick && 'cursor-pointer hover:opacity-80 transition-opacity',
        className,
      )}
      onClick={e => {
        e.stopPropagation();
        onItemClick?.(firstItem, e);
      }}
    >
      <span className="relative flex items-center gap-1 min-w-0">
        {isLive ? (
          <>
            {/* Live "at {event}" face — normal flow, drives the visible width. */}
            <span
              className={cn(
                'flex items-center gap-1 min-w-0',
                SWAP_MOTION.transition,
                entered ? SWAP_MOTION.shown : SWAP_MOTION.hidden,
              )}
            >
              <AttentionDot
                label="Live"
                className="bg-mb-live animate-live-pulse motion-reduce:animate-none"
              />
              <span className={NAME_CLASSES}>at {firstItem.name}</span>
            </span>
            {/* Space face fading out beneath it (§4.1 both states rendered).
                pointer-events-none (W2-5 M3): decorative overlay — it must never
                intercept the tap meant for the live chip beneath the cursor. */}
            {spaceFace && (
              <span
                aria-hidden="true"
                className={cn(
                  'pointer-events-none absolute inset-0 flex items-center gap-1 min-w-0',
                  SWAP_MOTION.transition,
                  entered ? SWAP_MOTION.hidden : SWAP_MOTION.shown,
                )}
              >
                <IndicatorAvatar item={spaceFace} avatarClassName={avatarClassName} />
                <span className={NAME_CLASSES}>{spaceFace.name}</span>
              </span>
            )}
          </>
        ) : (
          <span className="flex items-center gap-1 min-w-0">
            <IndicatorAvatar item={firstItem} avatarClassName={avatarClassName} />
            <span className={NAME_CLASSES}>{firstItem.name}</span>
          </span>
        )}
      </span>

      {/* +N suppressed while live (ruling 2026-07-10): the live row's tap
          navigates to the event page, so the overflow isn't inspectable there;
          the non-live row keeps it because its tap opens the SpacesSheet. */}
      {overflowCount > 0 && !isLive && (
        <span className="text-xs font-medium text-mb-charcoal shrink-0 whitespace-nowrap">
          +{overflowCount}
        </span>
      )}
    </button>
  );
};
